import { Models } from '@luxuryescapes/lib-flights-types'
import { journeyV2Map } from 'api/lib/journeyV2Map'
import { EmployeeTripRequestItem, EmployeeTripRequestsResponseResult } from 'businessTraveller/types/tripRequestTypes'

export function isHotelLikeTripRequestItem(item: EmployeeTripRequestItem): item is App.BusinessTraveller.AccommodationTripRequestItem {
  return item.type === 'accommodation' || item.type === 'bedbank'
}

export function mapTripRequest(
  request: EmployeeTripRequestsResponseResult,
  tripRequestItem: EmployeeTripRequestsResponseResult['tripRequestItems'][number],
  employees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>,
  hotelItems?: Array<App.BusinessTraveller.HotelItem>,
  tripRequestEmployees?: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>,
): App.BusinessTraveller.ApprovalRequest {
  const requester = employees.find((employee) => employee.id === request.requesterId)
  const mappedApprovalRequest: App.BusinessTraveller.ApprovalRequest = {
    ...request,
    details: { flightItems: tripRequestItem.type === 'flight' ? (tripRequestItem.details?.flightItems as Array<Models.Search.Fare>).map(item => journeyV2Map(item)) : undefined },
    requesterName: requester ? `${requester.firstName} ${requester.lastName}` : '',
    hotelItems,
    type: tripRequestItem.type === 'flight' ? 'flight' : 'hotel',
    employees: tripRequestEmployees,
  }
  return mappedApprovalRequest
}

export function mapApprovalRequest(
  request: EmployeeTripRequestsResponseResult,
  employees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>,
  hotelItems?: Array<App.BusinessTraveller.HotelItem>,
): App.BusinessTraveller.ApprovalRequest {
  const requester = employees.find((employee) => employee.id === request.requesterId)
  const mappedApprovalRequest: App.BusinessTraveller.ApprovalRequest = {
    ...request,
    details: { flightItems: request.type === 'flight' ? (request.details?.flightItems as Array<Models.Search.Fare>).map(item => journeyV2Map(item)) : undefined },
    requesterName: requester ? `${requester.firstName} ${requester.lastName}` : '',
    hotelItems,
  }
  return mappedApprovalRequest
}

export function constructFlightUrl(approvalRequest: App.BusinessTraveller.ApprovalRequest) {
  if (approvalRequest.status !== 'APPROVED') {
    return ''
  }

  const flightItems = approvalRequest.details?.flightItems || []
  const travellerCount = (approvalRequest.guestCount ?? 0) + (approvalRequest.employeeIds?.length ?? 0) || 1
  const firstFlight = flightItems[0]?.flightGroup?.flights[0]

  if (!flightItems.length || !firstFlight) {
    return ''
  }

  if (flightItems[0]?.fareType === 'MultiCity') {
    const params: any = {
      adults: travellerCount,
      children: 0,
      infants: 0,
      childrenAge: '',
      fareClass: mapFareClass(firstFlight.fareClass),
      fareType: 'multiCity',
      approvalRequest: approvalRequest.id,
    }

    // Add flight segments
    flightItems.forEach((item, index) => {
      const flight = item.flightGroup.flights[0]
      params[`flights[${index}][originAirportCode]`] = flight.departureAirport
      params[`flights[${index}][originAirportName]`] = flight.departureAirportName
      params[`flights[${index}][destinationAirportCode]`] = flight.arrivalAirport
      params[`flights[${index}][destinationAirportName]`] = flight.arrivalAirportName
      params[`flights[${index}][departDate]`] = flight.departingDate
    })

    return `/flights-search-results-multi-city?${new URLSearchParams(params).toString()}`
  }

  // else create URL for non-multi-city flights
  const lastFlight = flightItems.at(-1)?.flightGroup.flights[0]
  const lastFlightOfFirstGroup = flightItems[0].flightGroup.flights.at(-1)

  const flightSearchParams = new URLSearchParams({
    originAirportCode: firstFlight.departureAirport,
    originAirportName: firstFlight.departureAirportName,
    destinationAirportCode: lastFlightOfFirstGroup?.arrivalAirport || '',
    destinationAirportName: lastFlightOfFirstGroup?.arrivalAirportName || '',
    departDate: firstFlight.departingDate,
    returnDate: lastFlight?.departingDate || '',
    adults: travellerCount.toString(),
    children: '0',
    infants: '0',
    fareClass: mapFareClass(firstFlight.fareClass),
    fareType: flightItems.length > 1 ? 'return' : 'oneWay',
    approvalRequest: approvalRequest.id,
  })

  return `/flights-search-results?${flightSearchParams.toString()}`
}

function mapFareClass(fareClass: string) {
  const lowerCaseFareClass = fareClass.toLowerCase()

  if (lowerCaseFareClass === 'premium economy') {
    return 'premium'
  }

  if (lowerCaseFareClass.includes('business')) {
    return 'business'
  }

  if (lowerCaseFareClass.includes('first')) {
    return 'first'
  }

  return 'economy'
}

export function hotelProceedWithBookingUrl(tripRequest: App.BusinessTraveller.ApprovalRequest) {
  return `/checkout-restore-cart?cartId=${tripRequest.cartId}&approvalRequestId=${tripRequest.id}`
}

export function proceedWithBookingUrl(tripRequest: App.BusinessTraveller.ApprovalRequest): string | undefined {
  if (tripRequest) {
    switch (tripRequest.type) {
      case 'flight':
        return constructFlightUrl(tripRequest)
      case 'hotel':
        return hotelProceedWithBookingUrl(tripRequest)
      default:
        return undefined
    }
  }
  return undefined
}
